import {
  defaults,
  difference,
  each,
  filter,
  findWhere,
  isArray,
  isEmpty,
  map,
  pluck,
  uniq,
  without,
} from "underscore";
import {searchRow} from "../../components/table/tableHelpers";

export const getAllKeysArrayOfObjects = ({data, exclude}) => {
  const result = [];
  each(data, (item) => result.push(Object.keys(item).filter((each) => each !== exclude)));
  return [...new Set(result.flat())];
};

export const connectNullsWorkaround = ({data, keys}) => {
  const result = [];
  const nullDefaults = {};
  each(keys, (key) => (nullDefaults[key] = 0));
  each(data, (obj) => {
    const eachObj = {...obj};
    defaults(eachObj, nullDefaults);
    result.push(eachObj);
  });
  return result;
};

export const joinArrayWithCommasAnds = (wordArray) => {
  if (isEmpty(wordArray) || !isArray(wordArray)) {
    return "";
  }
  if (wordArray.length === 1) {
    return wordArray[0];
  }
  if (wordArray.length === 2) {
    return wordArray.join(" and ");
  }
  return wordArray.slice(0, -1).join(", ") + ", and " + wordArray[wordArray.length - 1];
};

export const taskAggregator = (taskAccumulator, taskOrJoinedTask) => {
  const obligationId = taskOrJoinedTask.obligation ? taskOrJoinedTask.obligation.id : null;
  const task = taskOrJoinedTask.task ? taskOrJoinedTask.task : taskOrJoinedTask;

  const foundTaskIndex = taskAccumulator.findIndex((foundTask) => task.id === foundTask.id);

  if (foundTaskIndex === -1) {
    const idToPush = obligationId ? [{obligationId}] : [];
    taskAccumulator.push({
      ...task,
      phaseOrdering: taskOrJoinedTask?.phaseOrdering,
      obligationIds: idToPush,
    });
    return taskAccumulator;
  }

  const existingFoundObligationIds = taskAccumulator[foundTaskIndex].obligationIds;

  if (obligationId !== null && !existingFoundObligationIds.includes({obligationId})) {
    existingFoundObligationIds.push({obligationId});
  }

  taskAccumulator[foundTaskIndex].obligationIds = existingFoundObligationIds;
  return taskAccumulator;
};

export const searchForItems = (searchValue, setValues, allValues) => {
  if (!searchValue) {
    setValues(allValues);
    return;
  }

  const escapeSearchTextForRegEx = (value) => value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
  const escapeFilterTextForRegEx = new RegExp(escapeSearchTextForRegEx(searchValue), "i");
  const filteredRows = allValues.filter((item) => searchRow(item, escapeFilterTextForRegEx, 0));

  setValues(filteredRows);
};

export const onSelectedItemsChange = (itemId, selectedItemIds, setSelectedItemIds) => {
  let itemIds = [];
  if (selectedItemIds.includes(itemId)) {
    itemIds = without(selectedItemIds, itemId);
    setSelectedItemIds(itemIds);
  } else {
    itemIds = [...selectedItemIds, itemId];
    setSelectedItemIds(itemIds);
  }

  return itemIds;
};

export const diffObjectsById = (existing, incoming, createKey) => {
  const idsToDelete = difference(
    existing.map((obj) => obj.id),
    incoming.map((obj) => obj.id),
  );
  let idsToCreate = difference(
    incoming.map((role) => role.id),
    existing.map((role) => role.id),
  );

  if (createKey) {
    idsToCreate = idsToCreate.map((id) => ({[createKey]: id}));
  }

  return {idsToDelete, idsToCreate};
};

export const generateCreateDeleteManyVariable = (
  itemsToCreate,
  itemsToDelete,
  idName,
  useExactDeleteItems = false,
) =>
  !isEmpty(itemsToCreate) || !isEmpty(itemsToDelete) ?
    {
      createMany: !isEmpty(itemsToCreate) ? {data: itemsToCreate} : undefined,
      deleteMany: !isEmpty(itemsToDelete) ?
        useExactDeleteItems ?
          itemsToDelete :
          {[idName]: {in: itemsToDelete}} :
        undefined,
    } :
    undefined;

export const getElementIdsForCreateManyAndElementEdgeIdsForDeleteMany = (
  existing,
  incoming,
  elementName,
  options = {},
) => {
  if (options.validateInitialValue && !options.initialValue) {
    return {toCreateMany: [], toDeleteMany: []};
  }

  const incomingMapped = pluck(incoming, "id");
  const existingMapped = map(existing, (item) => ({
    id: item.id,
    elementId: item[elementName],
  }));
  const existingElements = pluck(existingMapped, "elementId");

  const rawIdsToCreate = difference(incomingMapped, existingElements);
  const toCreateMany = map(rawIdsToCreate, (elementId) => ({
    [elementName]: elementId,
  }));

  const elementIdsToDelete = difference(existingElements, incomingMapped);
  const toDeleteMany = map(elementIdsToDelete, (id) => {
    const matchingExistingElement = findWhere(existingMapped, {elementId: id});
    return matchingExistingElement?.id;
  });

  return {toCreateMany, toDeleteMany};
};

export const mergeById = (existing, incoming) =>
  uniq([...existing, ...incoming], false, (n) => n.id);

export const includeDeletedOption = (options, item) => {
  if (item?.isDeleted) {
    return [...options, item];
  }
  return options;
};

export const includeDeletedTypes = (options, types) => {
  const deletedTypes = filter(types, (type) => type.type?.isDeleted);
  if (!isEmpty(deletedTypes)) {
    return [...options, ...map(deletedTypes, (type) => type.type)];
  }
  return options;
};
